import { Modal, Skeleton } from "antd";
import cloneDeep from "lodash/cloneDeep";
import React, { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useHttp } from "~hooks/useHttp";
import AddStandingsForm from "./AddStandingsForm";
import EditStandingsForm from "./EditStandingsForm";
import {
  columnsConfigBySportForData,
  columnsConfigBySportForDisplay,
  defaultCalculatedSchema,
  defaultPointsSchema,
  renameStandingPropertiesByConfig,
} from "./helpers";

const AddAndEditStandingsModal = ({
  handleCloseModal,
  openModal,
  standingId = null,
  handleSendRequest,
  okButtonLoading,
}) => {
  const { formatMessage } = useIntl();
  const { sport, organizationId, seasonId } = useParams();
  const { loading, request, error, cleanError } = useHttp();
  const [standings, setStandings] = useState(null);
  const [formRef, setFormRef] = useState(null);
  const [entityList, setEntityList] = useState(null);
  const [conferenceList, setConferenceList] = useState(null);
  const [calculatedSchema, setCalculatedSchema] = useState(null);

  const handleSetFormRef = (form) => setFormRef(form);

  const fetchStandings = useCallback(async () => {
    try {
      const res = await request({
        url: `/v1/${sport}/o/${organizationId}/standings/${standingId}?include=entities`,
      });
      setStandings(res?.data?.[0] || null);
    } catch (e) {
      console.error(e);
    }
  }, [request, sport, organizationId, standingId]);

  const fetchEntities = useCallback(async () => {
    try {
      const res = await request({
        url: `/v1/${sport}/o/${organizationId}/seasons/${seasonId}/entities?include=organization,entities,seasons&limit=1000`,
      });
      let entities = res?.data || [];
      const includes = res?.includes?.resources?.entities || [];
      entities = entities.map((entity) => {
        return includes[entity.entity.id] || entity;
      });

      setEntityList(entities);
    } catch (e) {
      console.error(e);
    }
  }, [request, sport, organizationId]);

  const fetchConferences = useCallback(async () => {
    try {
      const res = await request({
        url: `/v1/${sport}/o/${organizationId}/conferences`,
      });
      setConferenceList(res?.data || []);
    } catch (e) {
      console.error(e);
    }
  }, [request, sport, organizationId]);

  useEffect(() => {
    if (standingId) {
      fetchStandings();
    } else {
      fetchEntities();
      fetchConferences();
    }
  }, [fetchStandings, fetchEntities, fetchConferences, standingId]);

  useEffect(() => {
    let deepCloneDefaultCalculatedSchema = cloneDeep(defaultCalculatedSchema);
    if (standings?.calculated) {
      deepCloneDefaultCalculatedSchema = cloneDeep(standings.calculated);
    }

    const calculatedSchemaWithConfigApplied = Object.keys(deepCloneDefaultCalculatedSchema).reduce((acc, key) => {
      acc[key] = renameStandingPropertiesByConfig(
        deepCloneDefaultCalculatedSchema[key],
        columnsConfigBySportForDisplay[sport],
      );
      return acc;
    }, {});

    setCalculatedSchema(calculatedSchemaWithConfigApplied);
  }, [standings]);

  const handleOk = useCallback(async (form) => {
    const data = await form.validateFields();
    data.divisionId = data.divisionId || null;
    data.stageCode = data.stageCode ? data.stageCode.value : null;
    data.poolCode = data.poolCode ? data.poolCode.value : null;

    const deepCloneCalculatedValue = cloneDeep(JSON.parse(data.calculated));
    const calculatedValueConvertedForData = Object.keys(deepCloneCalculatedValue).reduce((acc, key) => {
      acc[key] = renameStandingPropertiesByConfig(deepCloneCalculatedValue[key], columnsConfigBySportForData[sport]);
      return acc;
    }, {});
    data.calculated = JSON.stringify(calculatedValueConvertedForData);
    await handleSendRequest(data);
    handleCloseModal();
  },[handleSendRequest, handleCloseModal])

  return (
    <Modal
      open={openModal}
      closable={true}
      destroyOnClose={true}
      onCancel={handleCloseModal}
      title={formatMessage({
        id: !standingId ? "standings.row.add" : "standings.row.update",
      })}
      okText={formatMessage({
        id: !standingId ? "standings.row.add.submit" : "standings.row.edit.submit",
      })}
      onOk={() => handleOk(formRef)}
      okButtonProps={{ loading: okButtonLoading }}
    >
      {!standingId ? (
        <AddStandingsForm
          handleSetFormRef={handleSetFormRef}
          entityList={entityList}
          conferenceList={conferenceList}
          defaultCalculatedSchema={calculatedSchema}
          defaultPointsSchema={defaultPointsSchema}
        />
      ) : standings ? (
        <EditStandingsForm
          handleSetFormRef={handleSetFormRef}
          standings={standings}
          entityList={entityList}
          defaultCalculatedSchema={calculatedSchema}
          defaultPointsSchema={defaultPointsSchema}
        />
      ) : (
        <Skeleton />
      )}
    </Modal>
  );
};

export default AddAndEditStandingsModal;
